import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/userStore';
import { useDayMeals } from '@/hooks/useMeals';
import type { Meditation } from '@/types/meditation';

interface Recommendation {
  meditation: Meditation;
  reason: string;        // короткое объяснение «почему именно этот»
  badgeText: string;     // ярлык наверху карточки
  showOnDashboard: boolean; // показывать ли на главном
}

/**
 * Определяет какую медитацию рекомендовать прямо сейчас
 * на основе времени и контекста (что юзер ел и т.д.)
 */
export function useContextualRecommendation() {
  const { profile } = useUserStore();
  const { meals } = useDayMeals();
  const [recommendation, setRecommendation] = useState<Recommendation | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile?.id) return;
    (async () => {
      const tz = (profile as any).timezone || 'Europe/Moscow';
      const hour = getLocalHour(tz);
      
      const slug = pickMeditationSlug(hour, meals);
      if (!slug) {
        setRecommendation(null);
        setLoading(false);
        return;
      }

      const { data } = await supabase
        .from('meditations')
        .select('*, category:meditation_categories(*)')
        .eq('slug', slug)
        .maybeSingle();
      
      if (data) {
        const meditation = data as Meditation;
        const { reason, badgeText, showOnDashboard } = buildContext(hour);
        setRecommendation({ meditation, reason, badgeText, showOnDashboard });
      }
      setLoading(false);
    })();
  }, [profile?.id, meals.length]);

  return { recommendation, loading };
}

function getLocalHour(timezone: string): number {
  try {
    return parseInt(
      new Intl.DateTimeFormat('en-US', { 
        timeZone: timezone, 
        hour: '2-digit', 
        hour12: false 
      }).format(new Date())
    );
  } catch {
    return new Date().getHours();
  }
}

function pickMeditationSlug(hour: number, meals: any[]): string | null {
  // 🌅 Утро (6-10): фокус для рабочего дня
  if (hour >= 6 && hour < 10) {
    return 'focus-2'; // Тихий рассвет
  }
  
  // ⚡ Рабочее утро (10-12): глубокая работа
  if (hour >= 10 && hour < 12) {
    return 'focus-1'; // Янтарный поток
  }
  
  // 🍽 Обед (12-14): лёгкий фон. Раньше здесь была голосовая «Тяга к еде» —
  // записи с голосом скрыты 13.10.2026 (миграция 066), в Релаксе только музыка.
  if (hour >= 12 && hour < 14) {
    return 'move'; // Тёплый ритм
  }
  
  // ⚡ После обеда (14-17): фокус
  if (hour >= 14 && hour < 17) {
    return 'focus-3'; // Стеклянная вода
  }
  
  // 🌬 Конец дня (17-20): снять стресс
  if (hour >= 17 && hour < 20) {
    return 'forest-light'; // Лесной свет
  }
  
  // 🌙 Вечер (20-22): подготовка ко сну
  if (hour >= 20 && hour < 23) {
    return 'meditation-music'; // Медитативная музыка
  }
  
  // 🌙 Поздний вечер / ночь (23-2): глубокое расслабление
  if (hour >= 23 || hour < 2) {
    return 'meditation-music'; // Медитативная музыка
  }
  
  // Ночь (2-6): ничего не рекомендуем (тихий час)
  return null;
}

function buildContext(hour: number): { reason: string; badgeText: string; showOnDashboard: boolean } {
  if (hour >= 6 && hour < 10) {
    return {
      reason: 'Спокойный фон для утренних задач',
      badgeText: '🌅 УТРО · НАСТРОЙСЯ НА ДЕНЬ',
      showOnDashboard: true,
    };
  }
  if (hour >= 10 && hour < 14) {
    return {
      reason: 'Удержит концентрацию во время работы',
      badgeText: '⚡ ДЛЯ ФОКУСА',
      showOnDashboard: false, // днём не показываем на главной
    };
  }
  if (hour >= 17 && hour < 20) {
    return {
      reason: 'Спокойная музыка, чтобы выдохнуть после дня',
      badgeText: '🌬 КОНЕЦ ДНЯ · СНЯТЬ НАПРЯЖЕНИЕ',
      showOnDashboard: true,
    };
  }
  if (hour >= 20 && hour < 23) {
    return {
      reason: 'Поможет отпустить день и подготовиться ко сну',
      badgeText: '🌙 ВЕЧЕР · ПОРА РАССЛАБИТЬСЯ',
      showOnDashboard: true,
    };
  }
  return {
    reason: 'Подходит для расслабления',
    badgeText: '✨ ДЛЯ ТЕБЯ СЕЙЧАС',
    showOnDashboard: false,
  };
}
